import styled, { css } from 'styled-components';

import colors from '@utils/colors';
import { mq } from '@utils/responsive';
import { H_PADDING_MOBILE } from '@utils/space';
import { Heading as UnstyledHeading, CTA } from '@components/type';
import UnstyledImage from 'gatsby-image';

export const Items = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  margin-right: 30px;

  ${mq.gtms`
    margin-right: 0;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `}
`;

export const ItemData = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`;

export const ItemImage = styled.div`
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  width: 50%;
  height: 50%;
  max-width: 350px;
  max-height: 350px;
  aspect-ratio: 1 / 1;
`;

export const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${mq.gtms`
    gap: 16px;
    align-items: center;
  `}

  &:nth-child(1) {
    & ${ItemData} {
      width: 50vw;
      max-width: 350px;

      ${mq.gtms`
        width: 100%;
        max-width: 270px;
      `}

      ${mq.gtlg`
        max-width: 350px;
      `}
    }

    & > ${ItemImage} {
      width: 50vw;
      height: 50vw;
      max-width: 350px;
      max-height: 350px;

      ${mq.gtms`
        width: 100%;
        max-width: 270px;
        max-height: 270px;
      `}

      ${mq.gtlg`
        max-width: 350px;
        max-height: 350px;
      `}
    }
  }

  &:nth-child(2) {
    ${mq.gtms`
        translate: 0 200px;
    `}

    & > ${ItemData} {
      width: 60vw;
      max-width: 500px;
      align-self: flex-end;
      margin-top: -10px;

      ${mq.gtms`
        width: 100%;
        max-width: 370px;
        margin-top: 0;
      `}

      ${mq.gtlg`
        max-width: 500px;
      `}
    }

    & > ${ItemImage} {
      width: 60vw;
      height: 60vw;
      max-width: 500px;
      max-height: 500px;
      align-self: flex-end;
      top: -10px;

      ${mq.gtms`
        top: 0;
        width: 100%;
        align-self: center;
        max-width: 370px;
        max-height: 370px;
      `}

      ${mq.gtlg`
        max-width: 500px;
        max-height: 500px;
      `}
    }
  }

  &:nth-child(3)  {
    & > ${ItemData} {
      width: 45vw;
      max-width: 300px;
      margin-top: -25px;

      ${mq.gtms`
        width: 100%;
        max-width: 270px;
        margin-top: 0;
      `}

      ${mq.gtlg`
        max-width: 350px;
      `}
    }

    & > ${ItemImage} {
      width: 45vw;
      height: 45vw;
      max-width: 300px;
      max-height: 300px;
      top: -25px;

      ${mq.gtms`
        width: 100%;
        top: 0;
        max-width: 270px;
        max-height: 270px;
      `}

      ${mq.gtlg`
        max-width: 350px;
        max-height: 350px;
      `}
    }
  }
`;

export const Title = styled(UnstyledHeading)`
  color: ${colors.B500};
  text-transform: uppercase;
  text-align: center;
  width: 100%;

  ${mq.gtms`
     font-size: 54px;
   `}
`;

export const Link = styled(CTA)`
  text-transform: uppercase;
  text-align: center;
  color: ${colors.B500};
  text-decoration: underline;
  font-size: 20px;
`;

export const Image = styled(UnstyledImage)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
  height: 100%;

  img {
    filter: brightness(80%) contrast(80%);
  }
`;

export const Content = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;

  ${mq.gtsm`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 0 ${H_PADDING_MOBILE};
  `}
`;

export const Inner = styled.div`
  position: relative;
  z-index: 1;
  padding: 18px ${H_PADDING_MOBILE};

  ${mq.gtmd`
    display: flex;
    padding: 0 calc((100vw - 1280px) / 2);
  `}

  & > svg {
    position: absolute;
    top: 20%;
    right: 0;
    left: 0;
    width: 100%;
    z-index: 0;
    stroke: ${colors.Y500};

    ${mq.gtsm`
      top: ${props => props.flipped ? 'auto' : '0'};
      bottom: ${props => props.flipped ? '0' : 'auto'};
    `}
  }
`;

export const Wrapper = styled.div`
  ${mq.gtms`
      padding-bottom: 200px;
    `}
  & > svg {
    z-index: 0;
  }

  & > svg:first-of-type {
    fill: ${(props) =>
      props.outline && props.outline !== 'none' ? 'none' : getThemeStyles(props.colorTheme).background};
    stroke: ${(props) =>
      props.outline === 'top' || props.outline === 'both'
        ? getThemeStyles(props.colorTheme).outline
        : 'none'};
    stroke-width: ${(props) =>
      props.outline === 'top' || props.outline === 'both' ? '3px' : 0};
    vertical-align: bottom;

    ${mq.gtsm`
      stroke-width: 1px;
    `}

    ${(props) =>
      props.outline && props.outline !== 'none' &&
      css`
        width: calc(100% + 6px);
        margin-right: -3px;
        margin-bottom: -3px;
        margin-left: -3px;
      `}
  }

  & > svg:last-of-type {
    fill: ${(props) =>
      props.outline && props.outline !== 'none' ? 'none' : getThemeStyles(props.colorTheme).background};
    stroke: ${(props) =>
      props.outline === 'bottom' || props.outline === 'both'
        ? getThemeStyles(props.colorTheme).outline
        : 'none'};
    stroke-width: ${(props) =>
      props.outline === 'bottom' || props.outline === 'both' ? '3px' : 0};
    transform: scale(-1, -1) translateY(1px);
    vertical-align: top;

    ${mq.gtsm`
      stroke-width: 1px;
    `}

    ${(props) =>
      props.outline &&
      css`
        width: calc(100% + 6px);
        margin-right: -3px;
        margin-top: -1px;
        margin-left: -3px;
      `}
  }
`;

export function getThemeStyles(theme) {
  switch (theme) {
    case 'white':
      return {
        outline: colors.B500,
        background: '#fff',
        swirl: colors.B100,
        fill: colors.B200,
        text: colors.B500,
      };
    case 'dark-blue':
      return {
        outline: colors.B500,
        background: colors.B500,
        fill: colors.B200,
        text: '#fff',
      };
    case 'dark-purple':
      return {
        outline: colors.P500,
        background: colors.P500,
        fill: colors.P200,
        text: '#fff',
      };
    case 'green':
      return {
        outline: colors.G100,
        background: colors.G100,
        fill: colors.G200,
        text: colors.G500,
      };
    case 'orange':
      return {
        outline: colors.O100,
        background: colors.O100,
        fill: colors.O200,
        text: colors.O500,
      };
    default:
      return {
        outline: colors.Y100,
        background: colors.Y100,
        fill: colors.Y200,
        text: colors.B500,
      };
  }
}
